import React, { PropsWithChildren } from 'react';
import { RouteComponentProps } from 'react-router';
import { connect } from 'react-redux';
import { ThunkDispatch } from 'redux-thunk';

import {
  DiscountLogModel,
  Driver,
  ReservationDetailViewModel,
  ReservationEditRequest,
  ReservationModel,
  ReservationStatusTypes
} from '../../../api/reservation/reservation.types';
import Breadcrumbs from '../../../components/breadcrumbs/Breadcrumbs';
import ConfirmationModal from '../../../components/modals/ConfirmationModal';
import { ReservationActionTypes } from '../types/reservation.types';
import { patchReservation } from '../actions/reservation.actions';
import { AppState } from '../../../store/store.types';
import { DiscountRuleModel } from '../../../api/pricing/pricing.types';
import SectionTitle from '../../../components/forms/SectionTitle';
import { reservationApi } from '../../../api/reservation/reservation.client';
import ReservationPaymentStatusLabel from '../../../components/forms/ReservationPaymentStatusLabel';
import JsonModal from '../../../components/modals/JsonModal';
import dayjs from 'dayjs';
import { WithTranslation, withTranslation } from 'react-i18next';
import { LoaderBar } from '../../../components/forms/LoaderBar';
import { formatDate, formatDateTime } from '../../../helpers/date.helper';
import { Link } from 'react-router-dom';
import { DATE_TIME_FORMAT } from '../../../api/api.types';
import ReservationStatusLabel from '../../../components/forms/ReservationStatusLabel';
import { ChargesTable } from './ChargesTable';


interface ReservationLocationProps {
  number: string;
  uuid?: string;
  supplier?: string;
}

interface ReservationDetailProps extends RouteComponentProps<ReservationLocationProps>, WithTranslation {
  selectedReservation: ReservationModel | null;
  patchReservation: (reservationNumber: string, request: ReservationEditRequest, uuid?: string, supplier?: string) =>
    Promise<void>;

}

interface ReservationDetailsState {
  isModalOpen: boolean;
  isBackendLogModalOpen: boolean;
  isPaymentLogModalOpen: boolean;
  isOtaApiLogModalOpen: boolean;
  isRateQuoteLogModalOpen: boolean;
  canEditSupplierRef: boolean;
  supplierRef: string;
  selectedReservation: ReservationModel | null;
  viewInfo: ReservationDetailViewModel | null;
  loading: boolean;
  discountRule: DiscountRuleModel | null;
}

class ReservationDetails extends React.Component<PropsWithChildren<ReservationDetailProps>, ReservationDetailsState> {

  readonly state = {
    isModalOpen: false,
    isBackendLogModalOpen: false,
    isPaymentLogModalOpen: false,
    isOtaApiLogModalOpen: false,
    isRateQuoteLogModalOpen: false,
    canEditSupplierRef: false,
    supplierRef: '',
    selectedReservation: null as unknown as ReservationModel,
    viewInfo: null,
    loading: false,
    discountRule: null
  };

  async componentDidMount() {
    const { uuid } = this.props.match.params;
    const { supplier } = this.props.match.params;
    const { number } = this.props.match.params;

    if (number) {
      this.setState({ loading: true });
      await reservationApi.Reservations.getReservationDetails(number, uuid, supplier).then((response) => {
        this.setState({
          selectedReservation: response.data.reservation,
          viewInfo: response.data,
          loading: false
        });
      });
    }

    if (this.state.selectedReservation) {
      const selectedReservation = this.state.selectedReservation as unknown as ReservationModel;
      this.setState({
        supplierRef: selectedReservation.supplierReferenceCode
      });
    }
  }

  findMainDriver = (drivers: Driver[]) => drivers.find((d) => !d.additional);


  getRateQuoteLogModal = () => {
    const { t } = this.props;
    if (this.state.selectedReservation) {
      const selectedReservation = this.state.selectedReservation as unknown as ReservationModel;
      if (selectedReservation.rateQuoteLog) {
        return (
          <JsonModal
            content={selectedReservation.rateQuoteLog}
            isOpen={this.state.isRateQuoteLogModalOpen}
            onClose={() => this.setState({ isRateQuoteLogModalOpen: false })}
            toggle={() => this.setState({ isRateQuoteLogModalOpen: false })}
            title={t('modal.title.rate_quote_log')}
          />
        );
      }
    }
  };

  getStatusButtons = () => {
    const { t } = this.props;

    if (this.state.selectedReservation) {
      const selectedReservation = this.state.selectedReservation as unknown as ReservationModel;
      if (selectedReservation.reservationStatusType === ReservationStatusTypes.PENDING) {
        return (
          <div>
            <button type="button" className="btn btn-danger mr-2"
                    onClick={() => this.onStatusChangeClick(ReservationStatusTypes.REQUEST_DENIED)}>
              {t('b.reservation.reject').toUpperCase()}
            </button>
            <button type="button" className="btn btn-primary edit-button"
                    onClick={() => this.onStatusChangeClick(ReservationStatusTypes.CONFIRMED)}>
              {t('b.reservation.confirm').toUpperCase()}
            </button>
          </div>
        );
      }

      if (selectedReservation.reservationStatusType === ReservationStatusTypes.CONFIRMED && dayjs(selectedReservation.pickupTimestamp).isBefore(dayjs())) {
        return (
          <div>
            <button type="button" className="btn btn-danger mr-2"
                    onClick={() => this.onStatusChangeClick(ReservationStatusTypes.NO_SHOW)}>
              {t('b.reservation.no_show').toUpperCase()}
            </button>
            <button type="button" className="btn btn-primary edit-button"
                    onClick={() => this.onStatusChangeClick(ReservationStatusTypes.COMPLETED)}>
              {t('b.reservation.complete').toUpperCase()}
            </button>
          </div>
        );
      }

      if (selectedReservation.reservationStatusType === ReservationStatusTypes.COMPLETED) {
        return (
          <div>
            <button type="button" className="btn btn-danger mr-2"
                    onClick={() => this.onStatusChangeClick(ReservationStatusTypes.NO_SHOW)}>
              {t('b.reservation.no_show').toUpperCase()}
            </button>
          </div>
        );
      }
    }

    return (<></>);
  };

  onStatusChangeClick = async (status: ReservationStatusTypes) => {
    const { number } = this.props.match.params;
    const { uuid } = this.props.match.params;
    const { supplier } = this.props.match.params;
    if (number) {
      this.setState({ loading: true });
      await this.props.patchReservation(number, { reservationStatusType: status }, uuid, supplier);
      reservationApi.Reservations.getReservationDetails(number, uuid, supplier).then((response) => this.setState({
        selectedReservation: response.data.reservation,
        viewInfo: response.data,
        canEditSupplierRef: false,
        loading: false
      }));
    }
  };

  onSupplierReferenceChange = async (value: string) => {
    const { number } = this.props.match.params;
    const { uuid } = this.props.match.params;
    const { supplier } = this.props.match.params;
    this.setState({ loading: true });
    if (number) {
      await this.props.patchReservation(number, { supplierReferenceCode: value }, uuid, supplier);
    }

    reservationApi.Reservations.getReservationDetails(number, uuid, supplier).then((response) => this.setState({
      selectedReservation: response.data.reservation,
      viewInfo: response.data,
      canEditSupplierRef: false,
      loading: false
    }));
  };


  getLogModals = () => {
    return (
      <>
        {this.getRateQuoteLogModal()}
      </>
    );
  };

  getDiscountText = (discount: DiscountLogModel) => {
    return (
      <div
        className="col-2 d-flex align-items-center justify-content-start list-link-item"
        onClick={() => window.open(`/rates/discountrules/${discount!.id}/view`, '_blank')}
      >
        {`${(discount.discountPercentage * 100).toFixed(2).replace(/\.00$/, '')} % (${formatDate(discount.pickupFrom)} - ${formatDate(discount.pickupTo)})`}
      </div>
    );
  };

  render() {
    const { uuid } = this.props.match.params;

    const viewInfo = this.state.viewInfo as unknown as ReservationDetailViewModel;
    let reservation: ReservationModel | undefined;
    let mainDriver: Driver | undefined;
    let discount: DiscountLogModel | undefined;
    if (this.state.selectedReservation) {
      reservation = this.state.selectedReservation as unknown as ReservationModel;
      mainDriver = this.findMainDriver(reservation.drivers);
      discount = reservation.discountLog.discounts.find((d) => d.applied);
    }

    const { t } = this.props;

    return (
      <>
        {uuid && (
          <>
            {this.props.children}
          </>
        )}
        <main>
          {this.state.loading && <LoaderBar/>}
          {!uuid && reservation && (
            <Breadcrumbs
              items={
                [
                  { path: '/reservations', label: 'Reservations' },
                  { path: null, label: reservation.reservationNumber }
                ]
              }
            />
          )}
          {reservation && (
            <div className="page-content">
              <div className="row justify-content-end">
                <div className="col-12 d-flex align-items-center justify-content-between">
                  <h1 className="mb-0">{t('l.reservation.reservation_no',
                    { reservationNo: reservation.reservationNumber })}</h1>
                  {this.getStatusButtons()}
                </div>
              </div>
              <SectionTitle title={t('section_title.reservation.reservation').toUpperCase()}/>
              <div className="d-flex">
                <div className="col-6 align-content-start">
                  <div className="d-flex col-12 row info-container justify-content-end">
                    <label className="col-4 d-flex align-items-center justify-content-end">
                      {t('l.reservation.reservation_number')}:
                    </label>
                    <div
                      className="col-4 d-flex align-items-center justify-content-start">{reservation.reservationNumber}</div>
                  </div>
                  <div className="d-flex col-12 row info-container justify-content-end">
                    <label className="col-4 d-flex align-items-center justify-content-end">
                      {t('l.reservation.reservation_status')}:
                    </label>
                    <div className="col-4 d-flex align-items-center justify-content-start">
                      <ReservationStatusLabel asText
                                              status={reservation.reservationStatusType}></ReservationStatusLabel>
                    </div>
                  </div>
                  <div className="d-flex col-12 row info-container justify-content-end">
                    <label className="col-4 d-flex align-items-center justify-content-end">
                      {t('l.reservation.broker')}:
                    </label>
                    <div className="col-4 d-flex align-items-center justify-content-start">
                      {viewInfo.broker ? viewInfo.broker.name : reservation.brokerId}
                    </div>
                  </div>
                  <div className="d-flex col-12 row info-container justify-content-end">
                    <label className="col-4 d-flex align-items-center justify-content-end">
                      {t('l.reservation.broker_ref')}:
                    </label>
                    <div className="col-4 d-flex align-items-center justify-content-start">
                      {reservation.brokerReferenceCode}
                    </div>
                  </div>
                  <div className="d-flex col-12 row info-container justify-content-end">
                    <label className="col-4 d-flex align-items-center justify-content-end">
                      {t('l.reservation.supplier_ref')}:
                    </label>
                    <div className="col-4 d-flex align-items-center justify-content-start">
                      {!this.state.canEditSupplierRef && (
                        <>
                          <div>{reservation.supplierReferenceCode}</div>
                          <button type="button" className="btn btn-text-only edit-button ml-1" onClick={() => {
                            this.setState({ canEditSupplierRef: true });
                          }}>{t('b.edit_Generic edit button')}
                          </button>
                        </>
                      )}
                      {this.state.canEditSupplierRef && (
                        <>
                          <input className="form-input form-control" value={this.state.supplierRef}
                                 onChange={(value: any) => this.setState({ supplierRef: value.target.value })}></input>
                          <button type="button" className="btn btn-text-only edit-button ml-1" onClick={() => {
                            this.onSupplierReferenceChange(this.state.supplierRef);
                          }}>{t('b.save_Generic save button translation')}
                          </button>
                          <button type="button" className="btn btn-text-only btn-text-only-gray ml-1" onClick={() => {
                            this.setState({
                              canEditSupplierRef: false,
                              supplierRef: reservation!.supplierReferenceCode
                            });
                          }}>{t('b.cancel_Generic cancel button translation')}
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                  <div className="d-flex col-12 row info-container justify-content-end">
                    <label className="col-4 d-flex align-items-center justify-content-end">
                      {t('l.reservation.broker')}:
                    </label>
                    <div className="col-4 d-flex align-items-center justify-content-start">
                      {viewInfo.broker ? viewInfo.broker.name : reservation.brokerId}
                    </div>
                  </div>
                </div>
                <div className="col-6 align-content-start">
                  <div className="d-flex col-12 row info-container justify-content-start">
                    <label className="col-4 d-flex align-items-center justify-content-end">
                      {t('l.reservation.pickup_time')}:
                    </label>
                    <div
                      className="col-6 d-flex align-items-center justify-content-start">{dayjs(reservation.pickupTimestamp).format(DATE_TIME_FORMAT)}</div>
                  </div>
                  <div className="d-flex col-12 row info-container justify-content-start">
                    <label className="col-4 d-flex align-items-center justify-content-end">
                      {t('l.reservation.dropoff_time')}:
                    </label>
                    <div
                      className="col-6 d-flex align-items-center justify-content-start">{dayjs(reservation.returnTimestamp).format(DATE_TIME_FORMAT)}</div>
                  </div>
                  <div className="d-flex col-12 row info-container justify-content-start">
                    <label className="col-4 d-flex align-items-center justify-content-end">
                      {t('l.reservation.pickup_location')}:
                    </label>
                    <div
                      className="col-6 d-flex align-items-center justify-content-start list-link-item"
                      onClick={() => window.open(`/setup/desks/${reservation!.pickupDeskCode}/view`, '_blank')}
                    >
                      {viewInfo.pickupDesk ? t('v.reservation.pickup_desk', {
                        deskName: viewInfo.pickupDesk.name,
                        deskCode: reservation.pickupDeskCode
                      }) : reservation.pickupDeskCode}
                    </div>
                  </div>
                  <div className="d-flex col-12 row info-container justify-content-start">
                    <label className="col-4 d-flex align-items-center justify-content-end">
                      {t('l.reservation.dropoff_location')}:
                    </label>
                    <div
                      className="col-6 d-flex align-items-center justify-content-start list-link-item"
                      onClick={() => window.open(`/setup/desks/${reservation!.returnDeskCode}/view`, '_blank')}
                    >
                      {viewInfo.returnDesk ? t('v.reservation.return_desk', {
                        deskName: viewInfo.returnDesk.name,
                        deskCode: reservation.returnDeskCode
                      }) : reservation.returnDeskCode}
                    </div>
                  </div>
                  <div className="d-flex col-12 row info-container justify-content-start">
                    <label className="col-4 d-flex align-items-center justify-content-end">
                      {t('l.reservation.car_class')}:
                    </label>
                    <div
                      className="col-6 d-flex align-items-center justify-content-start list-link-item"
                      onClick={() => window.open(`/setup/carmodels/${reservation!.carModelLog.carModelId}/view`, '_blank')}
                    >
                      {`${reservation.carModelLog.acrissCode}, ${reservation.carModelLog.name}`}
                    </div>
                  </div>
                </div>
              </div>
              <SectionTitle title={t('section_title.reservation.customer').toUpperCase()}/>
              <div className="d-flex">
                <div className="col-6 align-content-start">
                  <div className="d-flex col-12 row info-container justify-content-end">
                    <label className="col-4 d-flex align-items-center justify-content-end">
                      {t('l.reservation.customer_name')}
                    </label>
                    <div
                      className="col-4 d-flex align-items-center justify-content-start">{`${reservation.customer.firstName} ${reservation.customer.lastName}`}</div>
                  </div>
                  <div className="d-flex col-12 row info-container justify-content-end">
                    <label className="col-4 d-flex align-items-center justify-content-end">
                      {t('l.reservation.phone')}:
                    </label>
                    <div
                      className="col-4 d-flex align-items-center justify-content-start">{reservation.customer.phone}</div>
                  </div>
                  <div className="d-flex col-12 row info-container justify-content-end">
                    <label className="col-4 d-flex align-items-center justify-content-end">
                      {t('l.reservation.email')}:
                    </label>
                    <div
                      className="col-4 d-flex align-items-center justify-content-start">{reservation.customer.email}</div>
                  </div>
                  <div className="d-flex col-12 row info-container justify-content-end">
                    <label className="col-4 d-flex align-items-center justify-content-end">
                      {t('l.reservation.flight_number')}:
                    </label>
                    <div
                      className="col-4 d-flex align-items-center justify-content-start">{reservation.flightNumber}</div>
                  </div>
                  <div className="d-flex col-12 row info-container justify-content-end">
                    <label className="col-4 d-flex align-items-center justify-content-end">
                      {t('l.reservation.country_of_residence')}:
                    </label>
                    <div
                      className="col-4 d-flex align-items-center justify-content-start">{reservation.sourceCountryCode}
                    </div>
                  </div>
                </div>
                <div className="col-6 align-content-start">
                  <div className="d-flex col-12 row info-container justify-content-start">
                    <label className="col-4 d-flex align-items-center justify-content-end">
                      {t('l.reservation.main_driver_name')}:
                    </label>
                    <div
                      className="col-4 d-flex align-items-center justify-content-start">{mainDriver ? `${mainDriver.firstName} ${mainDriver.lastName}` : `${reservation.customer.firstName} ${reservation.customer.lastName}`}</div>
                  </div>
                  <div className="d-flex col-12 row info-container justify-content-start">
                    <label className="col-4 d-flex align-items-center justify-content-end">
                      {t('l.reservation.main_driver_age')}:
                    </label>
                    <div
                      className="col-4 d-flex align-items-center justify-content-start">
                      {reservation.driverAge}
                    </div>
                  </div>
                  <div className="d-flex col-12 row info-container justify-content-start">
                    <label className="col-4 d-flex align-items-center justify-content-end">
                      {t('l.reservation.main_driver_license_no')}:
                    </label>
                    <div
                      className="col-4 d-flex align-items-center justify-content-start">
                      {mainDriver ? mainDriver.licenseNo : ''}
                    </div>
                  </div>
                  <div className="d-flex col-12 row info-container justify-content-start">
                    <label className="col-4 d-flex align-items-center justify-content-end">
                      {t('l.reservation.comment_from_customer')}:
                    </label>
                    <div
                      className="col-4 d-flex align-items-center justify-content-start">
                      {reservation.customerSpecialReq}
                    </div>
                  </div>
                </div>
              </div>
              <SectionTitle title="PAYMENT"/>
              <div className="d-flex">
                <div className="col-6 align-content-start">
                  <div className="d-flex col-12 row info-container justify-content-end">
                    <label className="col-4 d-flex align-items-center justify-content-end">
                      Payment status:
                    </label>
                    <div className="col-4 d-flex align-items-center justify-content-start">
                      {reservation.lastPayment && (
                        <ReservationPaymentStatusLabel status={reservation.lastPayment.transactionStatus}/>
                      )}
                    </div>
                  </div>
                  <div className="d-flex col-12 row info-container justify-content-end">
                    <label className="col-4 d-flex align-items-center justify-content-end">
                      {t('l.reservation.transaction_id')}:
                    </label>
                    <div className="col-4 d-flex align-items-center justify-content-start">
                      {reservation.lastPayment?.transactionId}
                    </div>
                  </div>
                </div>
              </div>
              <SectionTitle title={t('section_title.reservation.rate_info').toUpperCase()}/>
              <div className="d-flex">
                <div className="col-6 align-content-start">
                  <div className="d-flex col-12 row info-container justify-content-end">
                    <label className="col-4 d-flex align-items-start justify-content-end">
                      {t('l.reservation.rate_plan')}:
                    </label>
                    <div
                      className="col-4 d-flex align-items-start justify-content-start">{viewInfo.ratePlan ?
                      t('v.reservation.rate_plan', {
                        ratePlanName: viewInfo.ratePlan.name,
                        ratePlanId: reservation.ratePlanId, interpolation: { escapeValue: false }
                      })
                      : reservation.ratePlanId}
                    </div>
                  </div>
                  <div className="d-flex col-12 row info-container justify-content-end">
                    <label className="col-4 d-flex align-items-center justify-content-end">
                      {t('l.reservation.rental_days')}:
                    </label>
                    <div
                      className="col-4 d-flex align-items-center justify-content-start">
                      {reservation.rentalDays}
                    </div>
                  </div>
                  {reservation.promoCodeLog.code ? (
                    <div className="d-flex col-12 row info-container justify-content-end">
                      <label className="col-4 d-flex align-items-center justify-content-end">
                        Promo code:
                      </label>
                      <div
                        className="col-4 d-flex align-items-center justify-content-start list-link-item"
                        onClick={() => window.open(`/rates/promocodes/${reservation!.promoCodeLog.code}/view`, '_blank')}>
                        {reservation.promoCodeLog.discountPercentage && reservation.promoCodeLog.discountPercentage > 0 ? `${reservation.promoCodeLog.code} (${reservation.promoCodeLog.discountPercentage * 100}%)` : ''}
                      </div>
                    </div>
                  ) : ''}
                </div>

                <div className="col-6 align-content-start">
                  <div className="d-flex col-12 row info-container justify-content-start">
                    <label className="col-4 d-flex align-items-center justify-content-end">
                      {t('l.reservation.broker_rate')}:
                    </label>
                    <div
                      className="col-6 d-flex align-items-center justify-content-start list-link-item"
                      onClick={() => window.open(`/setup/brokerrates/${reservation!.brokerRateLog.code}/view`, '_blank')}
                    >
                      {viewInfo.brokerRate ? `${viewInfo.brokerRate.name} (${reservation.brokerRateLog.code})`
                        : reservation.brokerRateLog.code}
                    </div>
                  </div>
                  {discount && (<div className="d-flex col-12 row info-container justify-content-start">
                    <label className="col-4 d-flex align-items-center justify-content-end">
                      Base rate discount:
                    </label>
                    {this.getDiscountText(discount)}
                  </div>)}
                </div>
              </div>

              {reservation ? (
                <ChargesTable
                  chargeLog={reservation.chargeLog}
                  mandatoryItems={reservation.mandatoryItemLog}
                  optionalItems={reservation.optionalItemLog}
                  conditionalItems={reservation.conditionalItemLog}
                  inclusiveItems={reservation.inclusiveItemLog}
                ></ChargesTable>
              ) : ''
              }


              <SectionTitle title={t('section_title.audit').toUpperCase()}/>
              <div className="d-flex row info-container">
                <label
                  className="col-4 d-flex align-items-center justify-content-end">{t('l.car_model.created_by')}: </label>
                <div className="col-2 d-flex align-items-center justify-content-start">
                  {reservation.createdBy ? reservation.createdBy : 'System'}{reservation.createdAt ? ` @ ${formatDateTime(reservation.createdAt)}` : ''}
                </div>
              </div>
              <div className="d-flex row info-container">
                <label
                  className="col-4 d-flex align-items-center justify-content-end">{t('l.car_model.modified_by')}: </label>
                <div className="col-2 d-flex align-items-center justify-content-start">
                  {reservation.modifiedBy ? reservation.modifiedBy : 'System'}{reservation.modifiedAt ? ` @ ${formatDateTime(reservation.modifiedAt)}` : ''}
                </div>
              </div>
              <div className="d-flex row info-container">
                <label
                  className="col-4 d-flex align-items-center justify-content-end">{t('l.reservation.audit_log')}: </label>
                <div className="col-2 d-flex align-items-center justify-content-start">
                  <Link className="btn btn-text-only"
                        to={`/reservations/${this.props.match.params.number}/view/audit`} target="_blank"
                        rel="noopener noreferrer">
                    {t('b.car_model.view_audit_log')}
                  </Link>
                </div>
              </div>
              <div className="d-flex row info-container">
                <label
                  className="col-4 d-flex align-items-center justify-content-end">{t('l.reservation.broker_logs')}: </label>
                <div className="col-2 d-flex align-items-center justify-content-start">
                  <Link className="btn btn-text-only"
                        to={`/reservations/${this.props.match.params.number}/view/logs/broker`} target="_blank"
                        rel="noopener noreferrer">
                    {t('b.reservation.broker_logs')}
                  </Link>
                </div>
              </div>
              <div className="d-flex row info-container">
                <label
                  className="col-4 d-flex align-items-center justify-content-end">{t('l.reservation.backend_integration_logs')}: </label>
                <div className="col-2 d-flex align-items-center justify-content-start">
                  <Link className="btn btn-text-only"
                        to={`/reservations/${this.props.match.params.number}/view/logs/backend_integration`}
                        target="_blank"
                        rel="noopener noreferrer">
                    {t('b.reservation.backend_integration_logs')}
                  </Link>
                </div>
              </div>
              <div className="d-flex row info-container">
                <label className="col-4 d-flex align-items-center justify-content-end">
                  {t('l.reservation.rate_request_log')}:
                </label>
                <div className="col-2 d-flex align-items-center justify-content-start">
                  <button type="button" className="btn btn-text-only" onClick={() => {
                    this.setState({ isRateQuoteLogModalOpen: true });
                  }}>
                    {t('b.reservation.view_rate_request_log')}
                  </button>
                </div>
              </div>
            </div>
          )}
          <ConfirmationModal
            title="Confirm item removal"
            content="Are you sure you want to remove this item?"
            isOpen={this.state.isModalOpen}
            toggle={() => this.setState({ isModalOpen: false })}
            onConfirm={() => {
            }}
            onCancel={() => this.setState({ isModalOpen: false })}
          />
          {this.getLogModals()}
        </main>
      </>
    );
  }
}

const mapStateToProps = (state: AppState) => ({
  selectedReservation: state.reservation.reservations.selectedReservation,
  brokers: state.admin.supplierBrokers.supplierBrokers
});

const mapDispatchToProps = (
  dispatch: ThunkDispatch<{}, {}, ReservationActionTypes>
) => ({
  patchReservation: (reservationNumber: string, request: ReservationEditRequest, uuid?: string, supplier?: string) =>
    dispatch(patchReservation(reservationNumber, request, uuid, supplier))
});

export default withTranslation()(connect(mapStateToProps, mapDispatchToProps)(ReservationDetails));
